import React from 'react';
import { motion } from 'framer-motion';

interface Module {
  title: string;
  description: string;
  emoji: string;
}

interface CourseContentProps {
  modules: Module[];
}

const CourseContent: React.FC<CourseContentProps> = ({ modules }) => {
  // Calculate progress for each module
  const getProgress = (index: number) => {
    const baseProgress = ((index + 1) / modules.length) * 100;
    return index === modules.length - 1 ? 100 : baseProgress;
  };

  return (
    <section className="py-20 bg-[#1B1F3B] relative overflow-hidden">
      {/* Background decoration */}
      <div className="absolute inset-0">
        <div 
          className="absolute inset-0 opacity-5"
          style={{
            backgroundImage: `radial-gradient(#1C98ED 1px, transparent 1px)`,
            backgroundSize: '24px 24px'
          }}
        ></div>
        <div className="absolute top-0 w-full h-px bg-gradient-to-r from-transparent via-[#1C98ED]/20 to-transparent"></div>
        <div className="absolute bottom-0 w-full h-px bg-gradient-to-r from-transparent via-[#1C98ED]/20 to-transparent"></div>
      </div>

      <div className="max-w-7xl mx-auto px-4 relative z-10">
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, ease: "easeOut" }}
        >
          <h2 className="text-4xl font-bold text-white mb-6">
            Course Curriculum
          </h2>
          <p className="text-xl text-gray-300 max-w-2xl mx-auto">
            Master cloud engineering through our carefully structured program
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 relative">
          {modules.map((module, index) => (
            <motion.div
              key={module.title}
              className="group relative"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ 
                duration: 0.4,
                delay: index * 0.1,
                ease: "easeOut"
              }}
            >
              {/* Module number badge */}
              <div className="absolute -top-4 -right-4 w-8 h-8 bg-[#1C98ED] rounded-full flex items-center justify-center text-white font-bold text-sm z-20 shadow-lg group-hover:scale-110 transition-transform duration-300">
                {index + 1}
              </div>

              {/* Card */}
              <div className="relative bg-[#252B4D] rounded-xl p-6 h-full border border-[#2A325A] group-hover:border-[#1C98ED]/50 transition-all duration-300 group-hover:transform group-hover:-translate-y-2 shadow-lg">
                {/* Glow effect */}
                <div className="absolute inset-0 rounded-xl bg-gradient-to-br from-[#1C98ED]/0 to-[#1C98ED]/5 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                
                <div className="relative z-10 flex flex-col h-full">
                  {/* Icon and Title */}
                  <div className="flex items-start gap-4 mb-4">
                    <div className="w-12 h-12 bg-[#1C98ED]/10 rounded-lg flex items-center justify-center backdrop-blur-sm">
                      <span className="text-2xl filter drop-shadow-lg">{module.emoji}</span>
                    </div>
                    <h3 className="text-lg font-bold text-white pt-2">
                      {module.title}
                    </h3>
                  </div>

                  {/* Description */}
                  <p className="text-gray-300 text-sm leading-relaxed mb-4 flex-grow">
                    {module.description}
                  </p>

                  {/* Module indicator */}
                  <div className="flex items-center gap-3 text-sm mt-auto">
                    <span className="text-[#1C98ED] font-medium whitespace-nowrap">
                      Module {index + 1}
                    </span>
                    <div className="h-0.5 flex-1 bg-[#1C98ED]/10 rounded-full overflow-hidden">
                      <div 
                        className="h-full bg-gradient-to-r from-[#1C98ED] to-[#1C98ED]/70 transition-all duration-700"
                        style={{ 
                          width: `${getProgress(index)}%`,
                          transition: 'width 0.7s ease-in-out'
                        }}
                      ></div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Connecting lines */}
              {index < modules.length - 1 && index % 4 !== 3 && (
                <div className="hidden lg:block absolute top-1/2 -right-4 w-8 h-0.5">
                  <div className="absolute inset-0 bg-gradient-to-r from-[#1C98ED]/30 to-transparent"></div>
                </div>
              )}
            </motion.div>
          ))}
        </div>

        {/* Start Learning button */}
        <motion.div
          className="mt-16 text-center"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.4, delay: modules.length * 0.1 }}
        >
          <button 
            className="bg-[#1C98ED] hover:bg-[#1C98ED]/90 text-white px-8 py-3 rounded-lg font-semibold transition-all duration-300 transform hover:scale-105 hover:shadow-lg hover:shadow-[#1C98ED]/20"
            onClick={() => {
              const pricingSection = document.querySelector('#pricing-section');
              if (pricingSection) {
                pricingSection.scrollIntoView({ behavior: 'smooth' });
              }
            }}
          >
            Start Your Journey
          </button>
        </motion.div>
      </div>
    </section>
  );
};

export default CourseContent; 